import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loading } from "./Loader";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategorySuggestions,
  selectCategorySuggestions,
  selectCategorySuggestionsStatus,
} from "../features/products/productsSlice";
import CategoryCreateModal from "./CategoryCreateModal";

/**
 * Выбор нескольких категорий: чипы выбранных + поиск с автоподсказками
 * + создание новой категории на месте.
 *
 * Контролируемый компонент — выбранные категории живут у родителя
 * в форме [{id, name}] и уходят в API как category_ids.
 */
export default function CategoryMultiSelect({ value, onChange, allowCreate = true }) {
  const dispatch = useDispatch();

  const suggestions = useSelector(selectCategorySuggestions);
  const suggestionsStatus = useSelector(selectCategorySuggestionsStatus);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const queryTrimmed = useMemo(() => query.trim(), [query]);
  useEffect(() => {
    if (!open) return;
    dispatch(fetchCategorySuggestions({ search: queryTrimmed, pageSize: 20 }));
  }, [open, queryTrimmed, dispatch]);

  const addCategory = useCallback(
    (category) => {
      if (!category?.id) return;
      if (selected.some((x) => String(x.id) === String(category.id))) return;
      onChange([...selected, { id: category.id, name: category.name || `#${category.id}` }]);
    },
    [onChange, selected]
  );

  const removeCategory = useCallback(
    (categoryId) => {
      onChange(selected.filter((x) => String(x.id) !== String(categoryId)));
    },
    [onChange, selected]
  );

  const onCloseCreate = useCallback(() => setCreateOpen(false), []);

  const onCategoryCreated = useCallback(
    (category) => {
      setCreateOpen(false);
      addCategory(category);
      setQuery("");
      setOpen(false);
    },
    [addCategory]
  );

  return (
    <div style={styles.wrap}>
      <div style={styles.chipsRow}>
        {selected.length === 0 ? (
          <div style={styles.hint}>Категории не выбраны</div>
        ) : (
          selected.map((c) => (
            <span key={c.id} style={styles.chip}>
              <span style={styles.chipText}>{c.name || `#${c.id}`}</span>
              <button type="button" onClick={() => removeCategory(c.id)} style={styles.chipX} title="Удалить категорию">
                ✕
              </button>
            </span>
          ))
        )}
      </div>

      <div style={styles.inputRow}>
        <div style={styles.suggestWrap}>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              window.setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={(e) => {
              // Внутри <form> (модалка товара) Enter иначе сабмитит форму.
              if (e.key === "Enter") e.preventDefault();
            }}
            style={styles.input}
            placeholder="Начните вводить название категории…"
          />
          {open && (
            <div style={styles.suggestBox}>
              {suggestionsStatus === "loading" && <Loading inline text="Загрузка…" />}
              {suggestionsStatus !== "loading" && (suggestions || []).length === 0 ? (
                <div style={styles.suggestHint}>Ничего не найдено</div>
              ) : (
                (suggestions || []).slice(0, 20).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    style={styles.suggestItem}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      addCategory(c);
                      setQuery("");
                      setOpen(false);
                    }}
                    title={`#${c.id}`}
                  >
                    <span style={styles.suggestName}>{c.name || "—"}</span>
                    <span style={styles.suggestMeta}>#{c.id}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {allowCreate && (
          <button type="button" onClick={() => setCreateOpen(true)} style={styles.createBtn}>
            + Новая категория
          </button>
        )}
      </div>

      {allowCreate && (
        <CategoryCreateModal
          open={createOpen}
          initialName={queryTrimmed}
          onCancel={onCloseCreate}
          onCreated={onCategoryCreated}
        />
      )}
    </div>
  );
}

const styles = {
  wrap: { display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
  chipsRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    fontSize: 14,
    fontWeight: 700,
  },
  chipText: { maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chipX: {
    width: 20,
    height: 20,
    borderRadius: 999,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 900,
    lineHeight: 1,
    fontSize: 13,
  },
  hint: { fontSize: 14, opacity: 0.7, fontWeight: 600 },
  inputRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  suggestWrap: { position: "relative", display: "flex", flexDirection: "column", flex: 1, minWidth: 0 },
  input: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  createBtn: {
    padding: "9px 14px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  suggestBox: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "calc(100% + 6px)",
    zIndex: 50,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "var(--c-bg)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    overflow: "hidden",
    maxHeight: 260,
    overflowY: "auto",
  },
  suggestItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    border: "none",
    borderBottom: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text)",
    cursor: "pointer",
    textAlign: "left",
    fontSize: 15,
    fontWeight: 700,
  },
  suggestName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  suggestMeta: { opacity: 0.7, fontWeight: 900, fontSize: 14 },
  suggestHint: { padding: "10px 12px", fontSize: 15, opacity: 0.75, fontWeight: 700 },
};
